import {
    ApexInfluencer,
    getApexInfluencers,
    getInstagramUserImages,
    getTiktokUserImages,
    InstagramUserImage,
    OrderingValues,
    TiktokUserImage,
} from '@round/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import { debounce } from 'lodash';

type UseApexInfluencersDataResult = {
    influencersData: ApexInfluencer[];
    page: number;
    influencersCount: number | undefined;
    hasNextInfluencersPage: boolean;
    influencersLoading: boolean;
    initialized: boolean;
    errorLoadingInfluencers: boolean;
    tiktokUserImages: TiktokUserImage[];
    instagramUserImages: InstagramUserImage[];

    loadNextPage: (requestInit?: RequestInit) => Promise<void>;
};

const defaultOrderingParams: OrderingValues<'most_booked' | 'tiktok_follower_count'>[] = [
    '-most_booked',
    '-tiktok_follower_count',
];

export function useApexInfluencersData({
    search,
    currency,
    campaign_type,
    platforms,
    category_tags,
    location_tags,
    min_followers,
    max_followers,
    min_cost,
    max_cost,
    content_tags,
}: Parameters<typeof getApexInfluencers>[0]): UseApexInfluencersDataResult {
    const [influencersData, setInfluencersData] = useState<ApexInfluencer[]>([]);
    const [influencersCount, setInfluencersCount] = useState<number>();
    const [initialized, setInitialized] = useState(false);
    const [influencersLoading, setInfluencersLoading] = useState(false);
    const [errorLoadingInfluencers, setErrorLoadingInfluencers] = useState(false);
    const [hasNextInfluencersPage, setHasNextInfluencersPage] = useState(false);
    const [tiktokUserImages, setTiktokUserImages] = useState<TiktokUserImage[]>([]);
    const [instagramUserImages, setInstagramUserImages] = useState<InstagramUserImage[]>([]);

    const [page, setPage] = useState(1);
    const tagsFilterValue = useMemo(() => {
        if (!Array.isArray(content_tags)) {
            return undefined;
        }

        const value = content_tags.reduce<number[][]>((acc, item) => {
            if (item.length) {
                return [...acc, item];
            }

            return acc;
        }, []);

        return value.length ? value : undefined;
    }, [content_tags]);

    // serialized tags are needed in dependency arrays, so we don't compare by reference, but by value.
    // otherwise we could add empty conditions, which would trigger new fetches
    // even though arrays may be equivalent.
    const serializedTagsFilterValue = JSON.stringify(tagsFilterValue);

    useEffect(
        function resetDataOnParamChange() {
            setInfluencersData([]);
            setInstagramUserImages([]);
            setTiktokUserImages([]);
            setInitialized(false);
            setPage(1);
        },
        [
            search,
            currency,
            campaign_type,
            platforms,
            category_tags,
            location_tags,
            min_followers,
            max_followers,
            min_cost,
            max_cost,
            serializedTagsFilterValue,
        ]
    );

    const fetchInfluencerImages = useCallback(
        async (influencers: ApexInfluencer[]): Promise<[TiktokUserImage[], InstagramUserImage[]]> => {
            const influencerTiktokUserIds = influencers
                .map((inf) => inf.tiktok_user_id)
                .filter((id): id is number => typeof id === 'number');
            const influencerInstagramUserIds = influencers
                .map((inf) => inf.instagram_user_id)
                .filter((id): id is number => typeof id === 'number');

            const fetchTiktokUserImages = async (ids: number[]) => {
                if (!ids.length) {
                    return [];
                }

                return getTiktokUserImages(ids);
            };

            const fetchInstagramImages = async (ids: number[]) => {
                if (!ids.length) {
                    return [];
                }

                return (await getInstagramUserImages(ids)).data;
            };

            const [tiktokImagesResult, instagramImagesResult] = await Promise.allSettled([
                fetchTiktokUserImages(influencerTiktokUserIds),
                fetchInstagramImages(influencerInstagramUserIds),
            ]);

            const tiktokImages: TiktokUserImage[] =
                tiktokImagesResult.status === 'fulfilled' ? tiktokImagesResult.value : [];
            const instagramImages: InstagramUserImage[] =
                instagramImagesResult.status === 'fulfilled' ? instagramImagesResult.value : [];

            return [tiktokImages, instagramImages];
        },
        []
    );

    const fetchInfluencerData = useCallback(
        async (...args: Parameters<typeof getApexInfluencers>) => {
            const [params, requestInit] = args;
            const pageSize = params.page === 1 ? 10 : 20;

            if (typeof params.currency !== 'number') {
                return;
            }

            try {
                setInfluencersLoading(true);
                setErrorLoadingInfluencers(false);
                const response = await getApexInfluencers(
                    { ...params, page_size: pageSize, ordering: defaultOrderingParams },
                    requestInit
                );

                if (response.status === 404) {
                    setErrorLoadingInfluencers(true);
                    return;
                }

                const {
                    data: { results: influencers, count, next },
                } = response;

                const [tiktokUserImages, instagramUserImages] = await fetchInfluencerImages(influencers);

                setTiktokUserImages((prev) => prev.concat(tiktokUserImages));
                setInstagramUserImages((prev) => prev.concat(instagramUserImages));
                setInfluencersData((prev) => prev.concat(influencers));
                setInfluencersCount(count);
                setHasNextInfluencersPage(Boolean(next));
            } catch {
                setErrorLoadingInfluencers(true);
            } finally {
                setInfluencersLoading(false);
                setInitialized(true);
            }
        },
        [fetchInfluencerImages]
    );

    const loadNextPage = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                await fetchInfluencerData(
                    {
                        page: page + 1,
                        search,
                        currency,
                        campaign_type,
                        platforms,
                        category_tags,
                        location_tags,
                        min_followers,
                        max_followers,
                        min_cost,
                        max_cost,
                        ordering: defaultOrderingParams,
                        content_tags: tagsFilterValue,
                    },
                    requestInit
                );
                setPage((page) => page + 1);
            } catch (e) {
                throw e;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fetchInfluencerData,
            page,
            search,
            currency,
            campaign_type,
            platforms,
            category_tags,
            location_tags,
            min_followers,
            max_followers,
            min_cost,
            max_cost,
            serializedTagsFilterValue,
        ]
    );

    const debouncedFetchInfluencerData = useMemo(() => debounce(fetchInfluencerData, 700), [fetchInfluencerData]);
    const debouncedLoadNextPage = useMemo(
        () => debounce(loadNextPage, 700, { leading: true, trailing: false }),
        [loadNextPage]
    );

    useAbortableEffect(
        (signal) => {
            debouncedFetchInfluencerData(
                {
                    search,
                    currency,
                    page: 1,
                    campaign_type,
                    platforms,
                    category_tags,
                    location_tags,
                    min_followers,
                    max_followers,
                    min_cost,
                    max_cost,
                    content_tags: tagsFilterValue,
                },
                { signal }
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            search,
            currency,
            campaign_type,
            platforms,
            category_tags,
            location_tags,
            min_followers,
            max_followers,
            min_cost,
            max_cost,
            debouncedFetchInfluencerData,
            serializedTagsFilterValue,
        ]
    );

    return {
        influencersData,
        page,
        influencersCount,
        hasNextInfluencersPage,
        influencersLoading,
        errorLoadingInfluencers,
        tiktokUserImages,
        instagramUserImages,
        initialized,
        loadNextPage: debouncedLoadNextPage,
    };
}
